import {
  Control,
  Controller,
  FieldErrors,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import { useRef, useState } from 'react';
import UsersAutocomplete from '../../shared/users-autocomplete';
import AvatarChip from '../../shared/avatar-chip';
import SharedTransferList from '../../shared/shared-transfer-list';
import { PolicyFormValues } from './policy-dialog';
import { useUserSettings } from '../../utils/use-user-settings';
import ExternalUsersInput from '../../shared/external-users-input';

import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

interface StepFourProps {
  control: Control<PolicyFormValues>;
  setValue: UseFormSetValue<PolicyFormValues>;
  getValues: UseFormGetValues<PolicyFormValues>;
  watch: UseFormWatch<PolicyFormValues>;
  errors: FieldErrors<PolicyFormValues>;
  domains: string[];
  trigger: UseFormTrigger<PolicyFormValues>;
  clearErrors: UseFormClearErrors<PolicyFormValues>;
}

export default function StepFour({
  control,
  setValue,
  getValues,
  watch,
  errors,
  domains,
  trigger,
  clearErrors,
}: StepFourProps) {
  const { allDomains } = useUserSettings();
  const autocompleteKey = useRef(new Date().getTime().toString());
  const [autoCompleteExcludeResponse, setAutoCompleteExcludeResponse] =
    useState('');
  const sources = watch('sources');
  const getDomain = (email: string) =>
    (email.split('@')[1] ?? '').toLowerCase();

  const externalUsers =
    sources?.filter(
      (source) =>
        source.includes('@') && !allDomains.includes(getDomain(source))
    ) ?? [];

  const internalUsers =
    sources?.filter(
      (source) =>
        !source.includes('@') || allDomains.includes(getDomain(source))
    ) ?? [];

  const sourceType = watch('sourceType');
  const targetsExcluded = watch('targetsExcluded');
  const allSource = watch('allSource');
  const actions = watch('actions') || [];

  function addSelection(emails: string[]) {
    const current = getValues('sources') ?? [];
    const uniqueEmails = Array.from(new Set([...current, ...emails]));
    setValue('sources', uniqueEmails);
    clearErrors();
    if (uniqueEmails.length === 0) {
      trigger('sources');
    }
  }

  function removeSelection(emails: string[]) {
    const current = getValues('sources') ?? [];
    const newList = current.filter((userEmail) => !emails.includes(userEmail));
    setValue('sources', newList);
  }

  const handleAddAllChecked = (value: boolean) => {
    setValue('allSource', value);

    if (value) {
      setValue('sources', []);
      setValue('targetsExcluded', []);
      clearErrors();
    }
  };

  return (
    <Stack gap={2} width={'100%'}>
      <Typography sx={{ mb: 1 }}>
        Select the targets you'd like these policy actions to be applied to. You
        can optionally add specific users to be excluded.
      </Typography>
      <Stack gap={2}>
        <Stack
          flexDirection={'row'}
          gap={4}
          alignItems={'center'}
          justifyContent="center"
        >
          <Controller
            name={`sourceType`}
            control={control}
            render={({ field: props }) => {
              return (
                <FormControl>
                  <FormLabel>
                    {sources!.length > 0
                      ? 'Target Type (Only one target type is allowed)'
                      : 'Target Type'}
                  </FormLabel>
                  <RadioGroup
                    aria-labelledby="radio-buttons-group-label"
                    defaultValue="User"
                    name="radio-buttons-group"
                    value={props.value}
                    onChange={(e) => {
                      props.onChange(e.target.value);
                      clearErrors();
                    }}
                    row
                  >
                    <FormControlLabel
                      value="User"
                      control={<Radio />}
                      label="User"
                      disabled={
                        sources!.length > 0 ||
                        externalUsers!.length > 0 ||
                        allSource === true
                      }
                    />
                    <FormControlLabel
                      value="Group"
                      control={<Radio />}
                      label="Group"
                      disabled={
                        sources!.length > 0 ||
                        externalUsers!.length > 0 ||
                        allSource === true
                      }
                    />
                    <FormControlLabel
                      value="OrgUnit"
                      control={<Radio />}
                      label="Org Unit"
                      disabled={
                        sources!.length > 0 ||
                        externalUsers!.length > 0 ||
                        allSource === true
                      }
                    />
                    <FormControlLabel
                      value="Domain"
                      control={<Radio />}
                      label="Domain"
                      disabled={
                        sources!.length > 0 ||
                        externalUsers!.length > 0 ||
                        allSource === true
                      }
                    />
                  </RadioGroup>
                </FormControl>
              );
            }}
          />
        </Stack>
        <Stack flexDirection={'row'} gap={1}>
          <Controller
            rules={{
              required: {
                value: actions.includes('Remove From Group')
                  ? !allSource && externalUsers.length === 0
                  : !allSource,
                message: actions.includes('Remove From Group')
                  ? 'Target is required unless an external user is added.'
                  : 'Target is required.',
              },
            }}
            name={'sources'}
            control={control}
            render={() => (
              <Box
                sx={{
                  flex: 1,
                  height: '40vh',
                }}
              >
                <SharedTransferList
                  sources={internalUsers}
                  addSelection={addSelection}
                  removeSelection={removeSelection}
                  handleAddAllChecked={handleAddAllChecked}
                  errors={!!errors.sources}
                  helperText={errors?.sources?.message || ' '}
                  allSource={allSource}
                  domains={domains || []}
                  sourceType={sourceType}
                  hasSelectAllOption={true}
                />
              </Box>
            )}
          />
        </Stack>

        <Stack flexDirection="row" justifyContent="space-evenly">
          {sourceType === 'User' && actions.includes('Remove From Group') && (
            <ExternalUsersInput
              value={externalUsers}
              onAdd={(email) => {
                setValue('sources', [...(getValues('sources') ?? []), email], {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }}
              onRemove={(email) => {
                const current = getValues('sources') ?? [];
                setValue(
                  'sources',
                  current.filter((source) => source !== email),
                  { shouldDirty: true, shouldValidate: true }
                );
              }}
            />
          )}
          <Stack gap={1} sx={{ mt: 2, ml: 2 }}>
            <Typography>Users to Exclude</Typography>
            <Stack flexDirection="row" gap={1}>
              <Controller
                name={'targetsExcluded'}
                control={control}
                render={() => (
                  <Box sx={{ width: 400 }}>
                    <UsersAutocomplete
                      newKey={`${autocompleteKey.current}-user-exclude`}
                      autocompleteValue={autoCompleteExcludeResponse}
                      setAutoCompleteValue={(value) => {
                        if (!value) return;
                        const currentExcluded =
                          getValues('targetsExcluded') ?? [];
                        if (!currentExcluded.includes(value)) {
                          setValue('targetsExcluded', [
                            ...currentExcluded,
                            value,
                          ]);
                        }
                        autocompleteKey.current = new Date()
                          .getTime()
                          .toString();
                        setAutoCompleteExcludeResponse('');
                      }}
                      disabled={allSource}
                    />
                  </Box>
                )}
              />
            </Stack>
            <Stack
              flexDirection="row"
              gap={1}
              flexWrap="wrap"
              sx={{ mt: 1, mb: 2 }}
              maxWidth={450}
            >
              {targetsExcluded && targetsExcluded.length > 0
                ? targetsExcluded.map((user) => {
                    return (
                      <AvatarChip
                        key={user}
                        label={user}
                        type={'User'}
                        email={user}
                        onDelete={() => {
                          const currentExcluded =
                            getValues('targetsExcluded') ?? [];
                          setValue(
                            'targetsExcluded',
                            currentExcluded.filter(
                              (excludedUser) => excludedUser !== user
                            )
                          );
                        }}
                      />
                    );
                  })
                : null}
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}
